'use client'

import { deliveryFrequencies } from '@entities/fulfillment'
import { Checkbox } from '@ui/base/checkbox'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldPanel,
  FieldSet,
  FieldTitle,
} from '@ui/base/field'
import { RadioGroup, RadioGroupItem } from '@ui/base/radio-group'
import { Separator } from '@ui/base/separator'
import { Textarea } from '@ui/base/textarea'
import { useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { MarketplaceSection } from './marketplace-section'
import { RequiredMark } from './required-mark'
import { ServiceAreaField } from './service-area-field'

const shipmentOptions = [
  ['fbo', 'FBO', 'Поставка на склады маркетплейсов'],
  ['fbs', 'FBS', 'Хранение и отгрузка заказов со своего склада'],
  ['dbs', 'DBS', 'Доставка заказов покупателям'],
  ['other', 'Другое', 'Другой формат работы'],
] as const

/** Второй шаг: схемы работы, их география и площадки. */
export const ShipmentStep = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  const shipmentTypes = watch('shipmentTypes') ?? []
  const deliveryFrequency = watch('deliveryFrequency')
  // При FBS и DBS заказ уезжает покупателю сразу — тут частота доставки обязательна.
  const needsDeliveryFrequency = shipmentTypes.includes('fbs') || shipmentTypes.includes('dbs')

  const toggleShipment = (type: FulfillmentFormValues['shipmentTypes'][number], checked: boolean) => {
    setValue('shipmentTypes', checked ? [...shipmentTypes, type] : shipmentTypes.filter((value) => value !== type), {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <>
      <FieldSet
        aria-invalid={Boolean(errors.shipmentTypes?.message)}
        aria-describedby={errors.shipmentTypes?.message ? 'fulfillment-shipment-types-error' : undefined}
      >
        <FieldLegend variant="block">Способы отгрузки</FieldLegend>
        <div className="space-y-4">
          {shipmentOptions.map(([value, label, description]) => {
            const active = shipmentTypes.includes(value)
            const shipmentId = `fulfillment-shipment-${value}`
            return (
              <section key={value} aria-label={`Способ отгрузки ${label}`} className="space-y-4">
                <FieldLabel htmlFor={shipmentId}>
                  <Field orientation="horizontal">
                    <Checkbox
                      id={shipmentId}
                      size="lg"
                      aria-labelledby={`${shipmentId}-title`}
                      aria-describedby={`${shipmentId}-description`}
                      checked={active}
                      onCheckedChange={(checked) => toggleShipment(value, checked === true)}
                    />
                    <FieldContent>
                      <FieldTitle id={`${shipmentId}-title`}>{label}</FieldTitle>
                      <FieldDescription id={`${shipmentId}-description`}>{description}</FieldDescription>
                    </FieldContent>
                  </Field>
                </FieldLabel>

                {/* Настройки выбранного способа лежат на подложке: так видно, что они относятся
                    к галочке выше, а не к шагу целиком. */}
                {active && (value === 'fbs' || value === 'dbs') && (
                  <FieldPanel className="gap-6">
                    <ServiceAreaField type={value} label={label} />

                    {value === 'fbs' && (
                      <FieldSet>
                        <FieldLegend variant="section">
                          Площадки <RequiredMark />
                        </FieldLegend>
                        <FieldDescription>
                          Для каждой площадки укажите, в какие федеральные округа вы возите.
                        </FieldDescription>
                        <MarketplaceSection />
                      </FieldSet>
                    )}
                  </FieldPanel>
                )}

                {active && value === 'other' && (
                  <FieldPanel>
                    <Field data-invalid={Boolean(errors.otherShipmentDescription?.message)}>
                      <FieldLabel htmlFor="fulfillment-other-shipment">
                        Описание другого способа отгрузки <RequiredMark />
                      </FieldLabel>
                      <Textarea
                        id="fulfillment-other-shipment"
                        rows={4}
                        aria-invalid={Boolean(errors.otherShipmentDescription?.message)}
                        aria-describedby={
                          errors.otherShipmentDescription?.message ? 'fulfillment-other-shipment-error' : undefined
                        }
                        {...register('otherShipmentDescription')}
                      />
                      <FieldError id="fulfillment-other-shipment-error">
                        {errors.otherShipmentDescription?.message}
                      </FieldError>
                    </Field>
                  </FieldPanel>
                )}
              </section>
            )
          })}
        </div>
      </FieldSet>
      <FieldError id="fulfillment-shipment-types-error">{errors.shipmentTypes?.message}</FieldError>

      {/* Черта стоит отдельным элементом до набора полей: внутри него заголовок разрывал бы её
          посередине — так браузер размещает legend внутри рамки. */}
      {needsDeliveryFrequency && (
        <div className="space-y-6">
          <Separator />
          <FieldSet>
            <FieldLegend>
              Как часто вы возите товар на склады маркетплейса <RequiredMark />
            </FieldLegend>
            <FieldDescription>
              Продавцы ищут фулфилменты, которые успевают отгрузить заказ в тот же день: отгрузка в пределах 12 часов
              даёт скидку площадки.
            </FieldDescription>
            <RadioGroup
              aria-invalid={Boolean(errors.deliveryFrequency?.message)}
              aria-describedby={errors.deliveryFrequency?.message ? 'fulfillment-delivery-frequency-error' : undefined}
              value={deliveryFrequency}
              onValueChange={(value) =>
                setValue('deliveryFrequency', value as FulfillmentFormValues['deliveryFrequency'], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              className="sm:grid-cols-2"
            >
              {deliveryFrequencies.map(({ code, label }) => {
                const frequencyId = `fulfillment-delivery-frequency-${code}`
                return (
                  <FieldLabel key={code} htmlFor={frequencyId}>
                    <Field orientation="horizontal">
                      <RadioGroupItem id={frequencyId} value={code} aria-labelledby={`${frequencyId}-title`} />
                      <FieldTitle id={`${frequencyId}-title`}>{label}</FieldTitle>
                    </Field>
                  </FieldLabel>
                )
              })}
            </RadioGroup>
            <FieldError id="fulfillment-delivery-frequency-error">{errors.deliveryFrequency?.message}</FieldError>
          </FieldSet>
        </div>
      )}
    </>
  )
}
